import React from "react";
import { Shield } from "lucide-react";
import { siteContact } from "@/config/site";
import LegalPage, { LegalSection, LegalList, LegalContactBox } from "@/components/shared/LegalPage";

const PrivacyPolicy = () => {
  return (
    <LegalPage
      eyebrow="Privacy"
      eyebrowIcon={Shield}
      title={<>Privacy <span className="gradient-text">Policy</span></>}
      lastUpdated="January 2026"
    >
      <LegalSection title="1. Introduction">
        <p>
          NameSpace Consultants ("we", "our", or "us") respects your privacy and is committed to protecting your
          personal data. This privacy policy explains how we collect, use, disclose, and safeguard your information
          when you use our services or visit our website.
        </p>
      </LegalSection>

      <LegalSection title="2. Information We Collect">
        <p>We may collect the following types of information:</p>
        <LegalList>
          <li><strong className="text-[var(--text-primary)]">Personal Information:</strong> Name, email address, phone number, company name, job title</li>
          <li><strong className="text-[var(--text-primary)]">Business Information:</strong> Company details, industry, business requirements</li>
          <li><strong className="text-[var(--text-primary)]">Technical Information:</strong> IP address, browser type, device information, cookies</li>
          <li><strong className="text-[var(--text-primary)]">Usage Data:</strong> How you interact with our website and services</li>
          <li><strong className="text-[var(--text-primary)]">Communication Data:</strong> Records of correspondence with us</li>
        </LegalList>
      </LegalSection>

      <LegalSection title="3. How We Use Your Information">
        <p>We use your personal data for the following purposes:</p>
        <LegalList>
          <li>To provide and maintain our services</li>
          <li>To communicate with you about our services</li>
          <li>To send you marketing communications (with your consent)</li>
          <li>To improve our website and services</li>
          <li>To comply with legal obligations</li>
          <li>To protect our rights and prevent fraud</li>
        </LegalList>
      </LegalSection>

      <LegalSection title="4. Data Sharing and Disclosure">
        <p>We do not sell your personal data. We may share your information with:</p>
        <LegalList>
          <li><strong className="text-[var(--text-primary)]">Service Providers:</strong> Third parties who perform services on our behalf</li>
          <li><strong className="text-[var(--text-primary)]">Legal Requirements:</strong> When required by law or to protect our rights</li>
          <li><strong className="text-[var(--text-primary)]">Business Transfers:</strong> In connection with mergers or acquisitions</li>
          <li><strong className="text-[var(--text-primary)]">With Your Consent:</strong> For any other purpose with your explicit consent</li>
        </LegalList>
      </LegalSection>

      <LegalSection title="5. Data Security">
        <p>
          We implement appropriate technical and organizational security measures to protect your personal data
          against unauthorized access, alteration, disclosure, or destruction. These measures include encryption,
          secure servers, access controls, and regular security assessments. However, no method of transmission
          over the Internet is 100% secure, and we cannot guarantee absolute security.
        </p>
      </LegalSection>

      <LegalSection title="6. Data Retention">
        <p>
          We retain your personal data only for as long as necessary to fulfill the purposes for which it was
          collected, including satisfying legal, accounting, or reporting requirements. The retention period may
          vary depending on the context of our relationship and our legal obligations.
        </p>
      </LegalSection>

      <LegalSection title="7. Your Rights">
        <p>Under applicable data protection laws, you have the following rights:</p>
        <LegalList>
          <li><strong className="text-[var(--text-primary)]">Access:</strong> Request access to your personal data</li>
          <li><strong className="text-[var(--text-primary)]">Rectification:</strong> Request correction of inaccurate data</li>
          <li><strong className="text-[var(--text-primary)]">Erasure:</strong> Request deletion of your personal data</li>
          <li><strong className="text-[var(--text-primary)]">Restriction:</strong> Request restriction of processing</li>
          <li><strong className="text-[var(--text-primary)]">Portability:</strong> Request transfer of your data</li>
          <li><strong className="text-[var(--text-primary)]">Objection:</strong> Object to certain processing activities</li>
          <li><strong className="text-[var(--text-primary)]">Withdraw Consent:</strong> Withdraw consent at any time</li>
        </LegalList>
      </LegalSection>

      <LegalSection title="8. Children's Privacy">
        <p>
          Our services are not directed to individuals under the age of 18. We do not knowingly collect personal
          information from children. If you become aware that a child has provided us with personal data, please
          contact us immediately.
        </p>
      </LegalSection>

      <LegalSection title="9. International Data Transfers">
        <p>
          Your information may be transferred to and processed in countries other than India. We ensure that
          appropriate safeguards are in place to protect your data in accordance with this privacy policy.
        </p>
      </LegalSection>

      <LegalSection title="10. Changes to This Policy">
        <p>
          We may update this privacy policy from time to time. We will notify you of any changes by posting the new
          policy on this page and updating the "Last updated" date. We encourage you to review this policy periodically.
        </p>
      </LegalSection>

      <LegalSection title="11. Contact Us">
        <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
        <LegalContactBox
          name="NameSpace Consultants"
          address={siteContact.address.full}
          email={siteContact.email}
          phone={siteContact.phone}
        />
      </LegalSection>
    </LegalPage>
  );
};

export default PrivacyPolicy;
